import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import type { MenuOptions } from "@circleco/compass/components/Menu";
import { Link } from "@tanstack/react-router";

import { useBuildNavItems } from "@/react/components/CircleAi/Dock/useBuildNavItems";
import { usePrimaryNavSection } from "@/react/components/CircleAi/Dock/useDockNavSections";
import { useIsCircleAIDockNavEnabled } from "@/react/components/CircleAi/Dock/useIsCircleAIDockNavEnabled";

import { useAdminMenuOptions } from "./useAdminMenuOptions";
import { useContentPageMenuLink } from "./useContentPageMenuLink";
import { useGetModerationMenu } from "./useGetModerationMenu";

export interface UseLogoMenuOptionsProps {
  isAdmin: boolean;
  isModerator: boolean;
  isWorkflowsEnabled: boolean;
  memberCount?: number;
  moderationCount?: number;
  showCustomizeTheme: boolean;
}

export const useLogoMenuOptions = ({
  isAdmin,
  isModerator,
  memberCount,
  moderationCount,
  showCustomizeTheme,
  isWorkflowsEnabled,
}: UseLogoMenuOptionsProps): MenuOptions => {
  const isCircleAIDockNavEnabled = useIsCircleAIDockNavEnabled();
  const primarySection = usePrimaryNavSection();
  const buildNavItems = useBuildNavItems();

  const moderation = useGetModerationMenu(moderationCount);
  const content = useContentPageMenuLink();

  const { currentCommunity } = usePunditUserContext();

  const isAffiliatesEnabled = Boolean(
    currentCommunity?.affiliates_feature_flag_enabled,
  );

  const hasAIAgentsAccess = Boolean(
    currentCommunity?.ai_agents_feature_flag_enabled,
  );

  const adminOptions = useAdminMenuOptions({
    memberCount,
    showCustomizeTheme,
    content,
    isWorkflowsEnabled,
    isAffiliatesEnabled,
    hasAIAgentsAccess,
    isCoursesV2Enabled: true,
  });

  if (isCircleAIDockNavEnabled && isAdmin) {
    const primaryItems: MenuOptions = primarySection.items.map(item => ({
      label: item.label,
      icon: item.icon,
      ...(item.to
        ? { renderLink: <Link to={item.to} /> }
        : { onClick: item.onClick }),
    }));

    const buildItems: MenuOptions = buildNavItems.map(item => ({
      label: item.label,
      icon: item.compassIcon,
      ...(item.children?.length
        ? {
            submenu: item.children.map(child => ({
              label: child.label,
              renderLink: <Link to={child.to} />,
            })),
          }
        : {
            renderLink: <Link to={item.path} />,
          }),
    }));

    const firstDivider = adminOptions.indexOf("divider");
    const tail = firstDivider === -1 ? [] : adminOptions.slice(firstDivider);
    const secondDivider = tail.indexOf("divider", 1);
    const settingsTail =
      secondDivider === -1 ? tail : tail.slice(0, secondDivider);
    const viewAsAndInvite =
      secondDivider === -1 ? [] : tail.slice(secondDivider + 1);

    return [
      ...primaryItems,
      "divider",
      ...viewAsAndInvite,
      "divider",
      ...buildItems,
      ...settingsTail,
    ];
  }

  if (isAdmin) {
    return adminOptions;
  }

  if (isModerator) {
    return [...content, moderation];
  }

  return [];
};
